import React from 'react';
import { Box, Button, Tooltip } from '@mui/material';
import { sortBy } from 'lodash';

import Table, { useStyles as useTableStyles } from 'components/Table';
import { numberToCurrencyString, dateToLabel } from 'further-ui/utils';
import { capitalize, lowerCase } from 'lodash';
import { displayFeeAccountingType } from 'utils/displayFeeAccounting';
import { Api, FeeCalculationBasis, FeeType } from 'further-types/investment';
import { CompareArrows } from '@mui/icons-material';
import useStyles from './styles';
import { useLoadMoreAndHideContent } from 'hooks/ui/useLoadMoreContent';
import { FeeCalculationBasisLabels } from 'further-ui/labels';
import FeePercentageContainer from './FeePercentageContainer';
import { FeeTotals } from './FeeTotals';

type Fee = Api.GetInvestmentFeesResponse['fees'][number] & {
  isTransferred: boolean;
};

const columns = [
  {
    label: 'Date',
    key: 'date',
    render: (fee: Fee) => dateToLabel(fee.date),
  },
  {
    label: 'Fee type',
    key: 'feeType',
    render: (fee: Fee) => capitalize(lowerCase(fee.feeType ?? undefined)),
  },
  {
    label: 'Fee name',
    key: 'name',
    render: (fee: Fee) => {
      if (!fee.sourceFee && !fee.isTransferred) return <>{fee.name}</>;

      const tooltipText = [
        ...(fee.sourceFee
          ? [
              `${numberToCurrencyString(fee.feeIncVAT)} was transferred from ${
                fee.sourceFee.investor.fullName
              } on ${dateToLabel(fee.sourceFee.investmentDate)}`,
            ]
          : []),
        ...(fee.isTransferred
          ? [
              `${numberToCurrencyString(fee.feeIncVAT)} was transferred to ${
                fee.investor.fullName
              } on ${dateToLabel(fee.investmentDate)}`,
            ]
          : []),
      ].join(' ');

      return (
        <>
          <Tooltip title={tooltipText}>
            <Box alignItems="center" display="flex" gap={4}>
              {fee.name} <CompareArrows fontSize="small" />
            </Box>
          </Tooltip>
        </>
      );
    },
  },
  {
    label: 'Fee %',
    key: 'grossFeePercentage',
    render: (fee: Fee) => {
      if (fee.feeType === FeeType.Transaction) {
        return `-`;
      }

      return <FeePercentageContainer fee={fee} />;
    },
  },
  {
    label: 'Fee £ (Net)',
    key: 'feeExVAT',
    render: (fee: Fee) => numberToCurrencyString(fee.feeExVAT),
  },
  {
    label: 'Fee £ (Gross)',
    key: 'feeIncVAT',
    render: (fee: Fee) => numberToCurrencyString(fee.feeIncVAT),
  },
  {
    label: 'Frequency',
    key: 'chargingFrequency',
    render: (fee: Fee) =>
      capitalize(lowerCase(fee.chargingFrequency ?? undefined)),
  },
  {
    label: 'Fee basis',
    key: 'feeCalculationBasis',
    render: (fee: Fee) => {
      return fee.feeType === FeeType.Recurring
        ? FeeCalculationBasisLabels[
            fee.feeCalculationBasis ||
              FeeCalculationBasis.PercentageOfTotalCommitment
          ]
        : '';
    },
  },
  {
    label: 'Starting period',
    key: 'startingPeriod',
  },
  {
    label: 'Finishing period',
    key: 'finishingPeriod',
  },
  {
    label: 'Fee accounting',
    key: 'feeAccounting',
    render: (fee: Fee) =>
      displayFeeAccountingType(fee.feeAccounting, fee.viaExit),
  },
  {
    label: 'Exported to custodian?',
    key: 'custodian',
    render: (fee: Fee) => (fee.isExportedToCustodian ? 'Yes' : 'No'),
  },
];

type Params = {
  fees: Api.GetInvestmentFeesResponse['fees'];
  loading: boolean;
};

const CoreFees: React.FC<Params> = ({ fees, loading }) => {
  const { classes: tableClasses } = useTableStyles();
  const { classes } = useStyles();

  //@ts-expect-error
  const combinedFees = sortBy(fees, 'date').reduce((combined, fee) => {
    return [
      ...combined,
      fee,
      ...(fee.transferredFees?.map((transferredFee) => ({
        ...transferredFee,
        isTransferred: true,
      })) ?? []),
    ];
  }, []);

  const { onToggle, displayedContent } =
    //@ts-expect-error
    useLoadMoreAndHideContent(combinedFees);

  return (
    <Table
      columns={columns.map((column) => ({
        ...column,
        getCellClassName: (fee: Fee) =>
          fee.isTransferred ? classes.inactiveCell : '',
      }))}
      //@ts-expect-error
      tablebody={displayedContent}
      loading={loading}
      variant="nohover"
      TableFooter={
        <tfoot>
          {
            //@ts-expect-error
            combinedFees.length > 10 ? (
              <tr className={tableClasses.tFootRow}>
                <td colSpan={12} align="center">
                  <Button color="primary" onClick={onToggle}>
                    {displayedContent.length > 10
                      ? 'Collapse charged fees table'
                      : ' Click here to show full table contents'}
                  </Button>
                </td>
              </tr>
            ) : null
          }
          <FeeTotals fees={fees} columnCount={columns.length} />
        </tfoot>
      }
    />
  );
};

export default CoreFees;
